/**
 * This displays a three-dot icon (horizontal in the iOS app, as iOS's own ⋯, vertical elsewhere)
 * which when clicked, shows a dropdown menu offering various actions and options to the user with
 * optional keyboard shortcuts.
 */
import * as preact from "preact";

import * as deviceDetect from "deviceDetect";
import * as buttonRole from "ui/buttonRole";
import Menu, { type MenuButtonProps, type MenuSection } from "ui/Menu";

export type MenuItem =
  | {
      type: "action";
      label: string;
      onClick: () => void;
      disabled?: boolean;
      /** Deletes or removes something: drawn in red */
      destructive?: boolean;
    }
  | {
      type: "toggle";
      label: string;
      onToggle: (value: boolean) => boolean | void; // Return false to prevent the toggle
      initialValue: boolean;
      disabled?: boolean;
      onDisabledClick?: () => void;
    }
  | {
      // Inline ‹ value › stepper. Chevrons don't close the menu so the user
      // can step through values and hear / see each one in place.
      type: "stepper";
      label: string;
      value: string;
      onPrev: () => void;
      onNext: () => void;
      prevDisabled?: boolean;
      nextDisabled?: boolean;
    }
  | { type: "separator" };

interface Props {
  items: MenuItem[];
  onOpen?: () => void;
}

interface State {
  // Per-item local toggle override, keyed by item index. Seeded from
  // `initialValue` on first read and updated on click so the checkmark
  // reflects clicks immediately and a second click computes `!override`
  // (a real toggle) even when the parent doesn't re-render between clicks
  // — many callers read store state non-reactively (peek, or signals not
  // wired up for the parent), so we can't assume the next render brings a
  // fresh prop. Cleared each time the menu opens so external changes are
  // picked up on re-open.
  toggleOverrides: { [index: number]: boolean };
}

class DropdownMenu extends preact.Component<Props, State> {
  state: State = { toggleOverrides: {} };

  /** The items as the menu's sections, split at its separators */
  private sections(): MenuSection[] {
    const { toggleOverrides } = this.state;
    const sections: MenuSection[] = [{ items: [] }];
    for (const [index, item] of this.props.items.entries()) {
      const items = sections[sections.length - 1].items;
      switch (item.type) {
        case "separator":
          sections.push({ items: [] });
          break;
        case "action":
          items.push({
            label: item.label,
            disabled: item.disabled,
            destructive: item.destructive,
            onSelect: item.onClick,
          });
          break;
        case "toggle": {
          const checked =
            index in toggleOverrides
              ? toggleOverrides[index]
              : item.initialValue;
          items.push({
            label: item.label,
            checked,
            disabled: item.disabled,
            onDisabledSelect: item.onDisabledClick,
            keepOpen: true,
            onSelect: () => {
              // onToggle may return `false` to veto (e.g. an upsell modal
              // blocked the change); don't apply the override in that case.
              if (item.onToggle(!checked) === false) return;
              this.setState({
                toggleOverrides: { ...toggleOverrides, [index]: !checked },
              });
            },
          });
          break;
        }
        case "stepper":
          items.push({
            label: item.label,
            stepper: {
              value: item.value,
              onPrevious: item.onPrev,
              onNext: item.onNext,
              previousDisabled: item.prevDisabled,
              nextDisabled: item.nextDisabled,
            },
          });
          break;
      }
    }
    return sections;
  }

  /**
   * The menu's button: iOS's ⋯ in the app (round dots, unlike the icon font's
   * square ones), ⋮ elsewhere
   */
  private renderButton = (buttonProps: MenuButtonProps) => {
    const attributes = {
      ...buttonProps,
      ...buttonRole.props,
      class: "menuButton",
      "aria-label": "Menu",
    };
    if (!deviceDetect.isIOSApp()) {
      return (
        <i {...attributes} class="icon icon-ellipsis-vertical menuButton"></i>
      );
    }
    return (
      <svg {...attributes} viewBox="0 0 24 24" fill="currentColor">
        <circle cx="5" cy="12" r="2" />
        <circle cx="12" cy="12" r="2" />
        <circle cx="19" cy="12" r="2" />
      </svg>
    );
  };

  render() {
    return (
      <Menu
        class="dropdownMenuContainer"
        sections={this.sections()}
        renderButton={this.renderButton}
        onOpen={() => {
          this.setState({ toggleOverrides: {} });
          this.props.onOpen?.();
        }}
      />
    );
  }
}

export default DropdownMenu;
